
import React, { useEffect, useMemo, useState } from 'react'

import { isArray, isEmpty, keyBy, omitBy } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useGetMerchantsSearchQuery } from 'src/_api/adminApi'
import { ServerAutoCompleteSearch } from 'src/_modules/common'

// `type` values already used by filters (InvoiceFilter radio, NotificationDialog role) -> backend `role`
const TYPE_TO_ROLE = { merchant: "BASIC", shipping_company: "SHIPPING_COMPANY" }

const EMPTY_LIST = { docs: [], hasNextPage: false }

// Accept both "MAPIT_SHIP" and ["MAPIT_SHIP"]; [] / "" mean "no filter"
const normalizeDeliveryMethod = (deliveryMethod) =>
    isArray(deliveryMethod) ? deliveryMethod.filter(Boolean).join(",") : deliveryMethod

const cleanParams = (params) => omitBy(params, v => !v || (isArray(v) && isEmpty(v)))

/**
 * Shared merchant select backed by the lightweight `/admin-panel/merchant/search-new` endpoint
 * ({ id, companyName } rows only). Emits `onChange({ target: { name, value } })` where `value`
 * is a merchant id (single) or an array of ids (`multiple`).
 */
export default function MerchantAutocomplete({
    name,
    value,
    onChange,
    label,
    multiple = false,
    deliveryMethod,
    type,
    role: roleProp,
    limit = 20,
    ...props
}) {
    const { t } = useTranslation()

    const role = TYPE_TO_ROLE[type] ?? roleProp
    const method = normalizeDeliveryMethod(deliveryMethod)

    const [params, setParams] = useState({ page: 1, limit, deliveryMethod: method, role })
    const [merchants, setMerchants] = useState(EMPTY_LIST)
    // id -> { id, companyName } for everything that has ever been selected/resolved,
    // so selections keep a stable object (and label) while the list is searched/paged.
    const [knownMerchants, setKnownMerchants] = useState({})
    const [inputValue, setInputValue] = useState("")

    const { data, isLoading, isFetching } = useGetMerchantsSearchQuery(cleanParams(params))

    const docs = merchants.docs

    // Accumulate pages; a page-1 result (new search / filter) replaces the list
    useEffect(() => {
        if (!data) return
        setMerchants(prev => {
            const incoming = data?.docs ?? []
            if (Number(data?.page) === 1) {
                return { ...data, docs: incoming }
            }
            const existingIds = new Set((prev?.docs ?? []).map(item => item?.id))
            return {
                ...data,
                docs: [...(prev?.docs ?? []), ...incoming.filter(item => !existingIds.has(item?.id))]
            }
        })
    }, [data])

    // Restart from page 1 when the server-side filters change
    useEffect(() => {
        setParams(prev =>
            prev.deliveryMethod === method && prev.role === role
                ? prev
                : { ...prev, page: 1, deliveryMethod: method, role }
        )
    }, [method, role])

    const valueIds = useMemo(() => {
        if (multiple) return isArray(value) ? value.filter(Boolean) : []
        return value ? [value] : []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiple, isArray(value) ? value.join(",") : value])

    // Ids we have no object for (e.g. preselected from URL/saved filters) -> resolve via `ids` lookup
    const missingIds = useMemo(
        () => valueIds.filter(id => !knownMerchants[id] && !docs.some(d => d?.id === id)),
        [valueIds, knownMerchants, docs]
    )
    const { data: lookup } = useGetMerchantsSearchQuery(
        { ids: missingIds.join(","), limit: missingIds.length },
        { skip: isEmpty(missingIds) }
    )

    useEffect(() => {
        setKnownMerchants(prev => {
            const additions = valueIds
                .filter(id => !prev[id])
                .map(id => docs.find(d => d?.id === id) ?? lookup?.docs?.find(d => d?.id === id))
                .filter(Boolean)
            return additions.length ? { ...prev, ...keyBy(additions, "id") } : prev
        })
    }, [valueIds, docs, lookup])

    const selectedOptions = useMemo(
        () => valueIds.map(id => knownMerchants[id]).filter(Boolean),
        [valueIds, knownMerchants]
    )

    // Keep selected merchants in the options even when the current page/search doesn't include them
    const options = useMemo(() => {
        const docIds = new Set(docs.map(d => d?.id))
        return [...selectedOptions.filter(m => !docIds.has(m.id)), ...docs]
    }, [selectedOptions, docs])

    const currentValue = multiple ? selectedOptions : (selectedOptions[0] ?? null)

    const handleChange = (newValue) => {
        const selected = multiple
            ? (isArray(newValue) ? newValue.filter(Boolean) : [])
            : (newValue ? [newValue] : [])

        setKnownMerchants(prev => {
            const additions = selected.filter(m => m?.id && !prev[m.id])
            return additions.length ? { ...prev, ...keyBy(additions, "id") } : prev
        })

        onChange({
            target: {
                name,
                value: multiple ? selected.map(m => m.id) : (newValue?.id ?? null)
            }
        })
    }

    return (
        <ServerAutoCompleteSearch
            hasNextPage={merchants?.hasNextPage}
            onLoadMore={() => setParams(prev => ({ ...prev, page: prev.page + 1 }))}
            onOpen={() => {
                // Restore the full list when reopening after a search filtered it.
                // Selected merchants stay visible via the options merge above.
                if (params.searchKey) {
                    setParams(prev => ({ ...prev, page: 1, searchKey: "" }))
                }
            }}
            label={label ?? t("Merchant")}
            fullWidth
            multiple={multiple}
            disableCloseOnSelect={multiple}
            ChipProps={multiple ? {
                variant: "outlined",
                color: "primary"
            } : undefined}
            getOptionLabel={(option) => option?.companyName ?? ""}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            inputValue={inputValue ?? ""}
            onInputChange={(searchValue) => {
                setParams(prev => ({ ...prev, page: 1, searchKey: searchValue }))
            }}
            onInputChangeSync={(searchValue) => {
                setInputValue(searchValue)
            }}
            onChange={handleChange}
            value={currentValue}
            size="small"
            loading={isLoading || isFetching}
            options={options}
            error={undefined}
            {...props}
        />
    )
}
